import * as Rx from 'rxjs/Rx'
import { Action, AnyAction } from 'typescript-fsa'
import { ActionsObservable, combineEpics } from 'redux-observable'
import { Store } from 'redux'
import { isEqual } from 'lodash'

import Dependencies from '../Dependencies'
import Epic from '../Epic'
import State from '../../states'
import {
  BlockAbsentEvent,
  BlockEvent,
  BlockPresentEvent,
  EventUpdate,
  isBlockAbsentEvent,
  isBlockEventUpdate,
  isBlockPresentEvent
} from '../../io/events'
import { EventHub, World } from '../../actions'
import { MAP_VIEW_GUTTER_RATIO } from '../constants'
import { Point, Rect, Size } from '../../models/BasicTypes'

export interface BlockID {
  x: number
  y: number
}

export function blockIDToStr (blockID: BlockID): string {
  return `${blockID.x}-${blockID.y}`
}

export function strToBlockID (str: string): BlockID {
  const [x, y] = str.split('-')
  return {
    x: parseInt(x, 10),
    y: parseInt(y, 10)
  }
}

/**
 * get block ID for given point in the map
 * @param blockSize size of map block
 * @param point point to get blockID from
 */
export const blockIDForPoint = (blockSize: Size, point: Point): BlockID => ({
  x: Math.floor(point.x / blockSize.width),
  y: Math.floor(point.y / blockSize.height)
})

/**
 * get block IDs for given region in the map
 * @param mapSize size of map
 * @param dimension dimension of map blocks
 * @param region to return block ids in it
 * @param inMapOnly only return block IDs in the map
 * @returns an array of block id in the given region
 */
export const blockIDsForRegion = (
  mapSize: Size,
  dimension: Size,
  region: Rect,
  inMapOnly: boolean = true
): Array<BlockID> => {
  const blockSize = {
    width: mapSize.width / dimension.width,
    height: mapSize.height / dimension.height
  }
  let leftTop = blockIDForPoint(blockSize, region.origin)
  let bottomRight = blockIDForPoint(blockSize, {
    x: region.origin.x + region.size.width,
    y: region.origin.y + region.size.height
  })
  if (inMapOnly) {
    if (leftTop.x < 0) {
      leftTop.x = 0
    }
    if (leftTop.y < 0) {
      leftTop.y = 0
    }
    if (bottomRight.x >= dimension.width) {
      bottomRight.x = dimension.width - 1
    }
    if (bottomRight.y >= dimension.height) {
      bottomRight.y = dimension.height - 1
    }
  }
  let blockIDs: BlockID[] = []
  if (bottomRight.x < leftTop.x || bottomRight.y < leftTop.y) {
    return blockIDs
  }
  for (let y = leftTop.y; y <= bottomRight.y; ++y) {
    for (let x = leftTop.x; x <= bottomRight.x; ++x) {
      blockIDs.push({ x, y })
    }
  }
  return blockIDs
}

// Update blocks
const updateBlocks = (
  action$: ActionsObservable<AnyAction>,
  store: Store<State>,
  { appContext }: Dependencies
) =>
  Rx.Observable
    .merge(
      action$
        .filter(World.viewPortUpdated.match)
        .filter(() => !!store.getState().world.config)
        .map(({ payload }) => payload),
      action$
        .filter(World.sessionCreated.match)
        .map(() => store.getState().world.viewPort)
    )
    .switchMap(viewPort => {
      const { legacyCRS } = appContext.clientSession!
      const legacyOrigin = legacyCRS.wanderToLegacy(viewPort.origin)
      const bottomRight = {
        x: viewPort.origin.x + viewPort.size.width,
        y: viewPort.origin.y + viewPort.size.height
      }
      const legacyBottomRight = legacyCRS.wanderToLegacy(bottomRight)
      const legacyViewPort = {
        origin: legacyOrigin,
        size: {
          width: legacyBottomRight.x - legacyOrigin.x,
          height: legacyBottomRight.y - legacyOrigin.y
        }
      }
      // TODO: extract this as a func
      const enlargedViewPort = {
        origin: {
          x:
            legacyViewPort.origin.x -
            legacyViewPort.size.width * MAP_VIEW_GUTTER_RATIO,
          y:
            legacyViewPort.origin.y -
            legacyViewPort.size.height * MAP_VIEW_GUTTER_RATIO
        },
        size: {
          width: legacyViewPort.size.width * MAP_VIEW_GUTTER_RATIO * 2,
          height: legacyViewPort.size.height * MAP_VIEW_GUTTER_RATIO * 2
        }
      }

      const state = store.getState()
      const config = state.world.config!
      const viewPortBlockIDs = new Set(
        blockIDsForRegion(
          config.mapInfo.size,
          config.mapInfo.dimension,
          enlargedViewPort
        ).map(blockIDToStr)
      )
      const currentBlockIDs = new Set(state.world.blocks.map(blockIDToStr))
      const toDelete = new Set(
        Array.from(currentBlockIDs).filter(x => !viewPortBlockIDs.has(x))
      )
      const toAdd = new Set(
        Array.from(viewPortBlockIDs).filter(x => !currentBlockIDs.has(x))
      )
      if (!toAdd.size && !toDelete.size) {
        return Rx.Observable.empty<AnyAction>()
      }
      return Rx.Observable.of(
        World.updateBlock({
          add: Array.from(toAdd).map(id => strToBlockID(id)),
          remove: Array.from(toDelete).map(id => strToBlockID(id))
        })
      )
    })

// Subscribe block event channels
const subscribeBlockChannels = (
  action$: ActionsObservable<AnyAction>,
  _store: Store<State>
) =>
  action$.filter(World.updateBlock.match).switchMap(action => {
    const actions = action.payload.add
      .map(
        blockID =>
          EventHub.subscribe({
            channel: `/blocks/${blockID.x}-${blockID.y}`,
            tag: 'block'
          }) as AnyAction
      )
      .concat(
        action.payload.remove.map(blockID =>
          EventHub.unsubscribe(`/blocks/${blockID.x}-${blockID.y}`)
        )
      )
    return Rx.Observable.from(actions)
  })

function isBlockEventUpdateAction (
  action: Action<any>
): action is Action<EventUpdate<BlockEvent>> {
  return isBlockEventUpdate(action.payload)
}

function isBlockPresentEventUpdate (
  update: EventUpdate<BlockEvent>
): update is EventUpdate<BlockPresentEvent> {
  return isBlockPresentEvent(update.event)
}

function isBlockAbsentEventUpdate (
  update: EventUpdate<BlockEvent>
): update is EventUpdate<BlockAbsentEvent> {
  return isBlockAbsentEvent(update.event)
}

// Handle block event
const handleBlockEvents = (
  action$: ActionsObservable<AnyAction>,
  store: Store<State>
) =>
  action$
    .filter(EventHub.eventUpdate.match)
    .filter(isBlockEventUpdateAction)
    .map(action => action.payload)
    // TODO: unfold snapshots here
    .multicast(
      () => new Rx.Subject<EventUpdate<BlockEvent>>(),
      blockEventsUpdate$ =>
        blockEventsUpdate$
          .filter(isBlockPresentEventUpdate)
          .mergeMap(update => {
            const presentEvent = update.event
            const { mapObject } = presentEvent
            const absent = blockEventsUpdate$.filter(
              absentUpdate =>
                isBlockAbsentEventUpdate(absentUpdate) &&
                absentUpdate.channel === update.channel &&
                absentUpdate.event.mapObject.id === mapObject.id
            )
            return (
              Rx.Observable
                .of(update)
                // We don't want map object that appears and suddently disappear,
                // so that we wait 300 ms to see if the map object is still alive
                // then emit add map object event
                .delay(300)
                .takeUntil(absent)
            )
          })
    )
    // just for casting type
    .filter(isBlockPresentEventUpdate)
    .concatMap(({ event }) => {
      if (event.mapObject.id in store.getState().world.mapObjects) {
        return Rx.Observable.empty()
      }
      return Rx.Observable.of(
        World.addMapObject({
          id: event.mapObject.id,
          type: event.mapObject.type
        })
      )
    })

// Update block for join, so that if the join is out side of view port, we can
// load the player in the block
const updateBlockForJoin = (
  action$: ActionsObservable<AnyAction>,
  store: Store<State>
) =>
  action$
    .filter(World.sessionCreated.match)
    .combineLatest(action$.filter(World.join.done.match))
    .switchMap(([_, { payload: { params } }]) => {
      const state = store.getState()
      const config = state.world.config!
      const { size, dimension } = config.mapInfo
      const blockSize = {
        width: size.width / dimension.width,
        height: size.height / dimension.height
      }
      const blockID = blockIDForPoint(blockSize, params)
      // Do not add the block if it's already there
      if (
        state.world.blocks.findIndex(point => isEqual(blockID, point)) !== -1
      ) {
        return Rx.Observable.empty()
      }
      return Rx.Observable.of(
        World.updateBlock({
          add: [blockID],
          remove: []
        })
      )
    })

export default combineEpics<Epic>(
  updateBlocks,
  subscribeBlockChannels,
  handleBlockEvents,
  updateBlockForJoin
)



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/epics/world/block.ts